import { useTokenStore } from 'Shared/hooks/useTokenStore';
import { Analytics } from 'Shared/utils/client';
import { LINK } from '@constants';
import { Bell, Box, Button, Column, Icon, Row, Typography, useTheme } from '@viewup/ui';
import { useGlobalModalStore } from 'app/_hook';
import { NotificationItem } from 'app/notifications/_components';
import { AllReadNotificationButton } from 'app/notifications/_components/AllReadNotificationButton';
import { useGetNotification } from 'app/notifications/_features/api';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

export const HeaderNotification = () => {
  const theme = useTheme();
  const router = useRouter();
  const pathname = usePathname();

  const { accessToken } = useTokenStore();
  const [isOpenNotification, setIsOpenNotification] = useState(false);

  const { data: getNotifications, refetch } = useGetNotification({ limit: 20 });

  const { getValue } = useGlobalModalStore();
  const { setConfirmModal } = getValue('actions');

  const isShowUnreadDot =
    (getNotifications?.data.filter((notification) => !notification.isRead).length || 0) > 0;

  useEffect(() => {
    if (!isOpenNotification) return;
    refetch();
  }, [isOpenNotification]);

  useEffect(() => {
    setIsOpenNotification(false);
    if (accessToken) {
      if (
        pathname === LINK.VIEWING_AUTO_PROGRESS ||
        pathname === LINK.VIEWING_AUTO_SETTING ||
        pathname === LINK.VIEWING_CLICK
      )
        return;
      refetch();
    }
  }, [pathname]);

  const handleClickNotification = () => {
    if (!accessToken) {
      setConfirmModal({
        content: `본 서비스는 회원만 이용이 가능해요.\n로그인할까요?`,
        onClickFillButton: () => {
          router.replace(LINK.LOGIN);
        },
      });
      return;
    }

    if (!isOpenNotification) Analytics.getInstance().track('MAIN_HEADER_NOTIFICATION');
    setIsOpenNotification((p) => !p);
  };

  return (
    <Box height={'100%'} position='relative' width={24}>
      <Box height={'100%'} position='relative' width={'100%'}>
        <Icon
          css={{ cursor: 'pointer', top: '50%', transform: 'translateY(-50%)' }}
          onClick={handleClickNotification}
          position='absolute'
          size='XS'
          source={Bell}
          // token 방식의 z-index를 고정값으로 변경합니다.
          // theme.zIndex.popover[1054]
          // theme이 client에서만 접근이 가능하여 hydration mismatch 오류가 발생하므로 고정값으로 설정합니다.
          // 이후 theme을 server에서도 접근 가능하도록 수정하는 편이 좋을 것 같습니다.
          zIndex={1054}
        />
        {isShowUnreadDot && (
          <Box
            bgColor='bg.danger'
            css={{ borderRadius: 5, top: 10 }}
            height={4}
            position='absolute'
            right={0}
            width={4}
          />
        )}
      </Box>

      {isOpenNotification && (
        <>
          {/* 배경 오버레이 */}
          <Box
            bgColor='bg.dimmer.20'
            css={{
              left: '50%',
              transform: 'translateX(-50%)',
            }}
            height={'100%'}
            left={0}
            maxWidth={440}
            onClick={() => setIsOpenNotification(false)}
            position='fixed'
            top={0}
            width={'100%'}
            zIndex={theme.zIndex.popover[1054]}
          />

          {/* 노티피케이션 내용 */}
          <Column
            alignItems='center'
            bgColor='bg.primary'
            br={8}
            css={{
              boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0 .20)',
            }}
            gap={8}
            p={16}
            position='absolute'
            right={0}
            width={320}
            zIndex={theme.zIndex.popover[1055]}
          >
            <Row alignItems='center' justifyContent='space-between' width={'100%'}>
              <Typography color='text.primary' fontType='body.medium'>
                새로운 알림
              </Typography>
              <AllReadNotificationButton
                notificationUuids={
                  getNotifications?.data.reduce<string[]>((acc, cur) => {
                    if (!cur.isRead) acc.push(cur.notificationUuid);
                    return acc;
                  }, []) || []
                }
                size={'XS'}
              />
            </Row>

            {/* 알림 목록 */}
            <Column css={{ overflow: 'auto' }} maxHeight={240} mb={32} width={'100%'}>
              {getNotifications?.data?.map((notification) => (
                <NotificationItem
                  itemPadding={16}
                  key={notification.notificationUuid}
                  {...notification}
                />
              ))}
            </Column>

            <Button
              onClick={() => {
                setIsOpenNotification(false);
                router.push(LINK.NOTIFICATIONS);
              }}
              size={'XS'}
              styledComponents={{ root: { bottom: 16, position: 'absolute' } }}
              variant='ghost.assistive'
            >
              모든 알림 보기
            </Button>
          </Column>
        </>
      )}
    </Box>
  );
};
